import { CONSTANTS } from "@/constants/constants"
import { useEnrollIntoFreeMaterialCourseMutation } from "@/queries/mutations/freeMaterialsMutations"
import {
  useFreeMaterialSettings,
  useStudentFreeMaterialEnrollments,
} from "@/queries/queries/freeMaterialsQueries"
import type { FreeMaterialCourse } from "@/types/freeMaterialsTypes"
import { Badge, Button, Card, Col, Spinner } from "react-bootstrap"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router"

export default function FreeMaterialCourse({
  course,
}: {
  course: FreeMaterialCourse
}) {
  const navigate = useNavigate()
  const { t } = useTranslation()

  const studentFreeMaterialEnrollmentQuery = useStudentFreeMaterialEnrollments()
  const enrollIntoFreeMaterialCourseMutation =
    useEnrollIntoFreeMaterialCourseMutation()

  const freeMaterialSettingsQuery = useFreeMaterialSettings()

  const enrollmentWhatsappNumber = freeMaterialSettingsQuery.data?.find(
    (setting) => setting.key === CONSTANTS.FREE_MATERIAL_ENROLLMENT_NUMBER
  )

  const isFreeCourse = Number(course?.price) === 0 || course?.payment_type === 1
  const isStudentEnrolled = studentFreeMaterialEnrollmentQuery.data?.some(
    (enrollment) => enrollment.free_material_course_id === course?.id
  )

  const enrollUrl = `https://wa.me/${enrollmentWhatsappNumber?.value}?text=${CONSTANTS.FREE_MATERIAL_ENROLLMENT_MESSAGE} "${course?.name}"`

  return (
    <Col md={3}>
      <Card style={{ width: "100%", minHeight: "36rem" }}>
        {Number(course?.price) === 0 && (
          <Badge
            bg="success"
            className="position-absolute fs-6"
            style={{ top: "10px", left: "10px" }}
            pill
          >
            {t("freeMaterialsPage.freeCourse")}
          </Badge>
        )}
        <Card.Img
          className="object-fit-cover"
          style={{ height: "16rem" }}
          variant="top"
          src={course?.image || "public/assets/images/placeholder.png"}
          onError={(e) => {
            e.currentTarget.src = "public/assets/images/placeholder.png"
          }}
        />
        <Card.Body className="d-flex flex-column">
          <Card.Title className="fw-bold fs-4">{course?.name}</Card.Title>
          <Card.Text className="text-secondary text-truncate">
            {course?.description}
          </Card.Text>
          <div className="mt-auto">
            {isStudentEnrolled ? (
              <>
                <Badge bg="success" className="fs-5">
                  {course.progress}% {t("freeMaterialsPage.completed")}
                </Badge>
              </>
            ) : (
              <div>
                {course?.payment_type === 1 && Number(course?.price) !== 0 ? (
                  <>
                    <p
                      className="text-body-tertiary fs-3 fw-bold me-1"
                      style={{ textDecoration: "line-through" }}
                    >
                      {course?.price} EGP
                    </p>
                    <p className="text-success fs-3 fw-bold me-1">
                      {t("freeMaterialsPage.free")}
                    </p>
                  </>
                ) : course?.payment_type === 1 &&
                  Number(course?.price) === 0 ? (
                  <p className="text-success fs-3 fw-bold me-1">
                    {t("freeMaterialsPage.free")}
                  </p>
                ) : (
                  <>
                    <p className="text-success fs-3 fw-bold me-1">
                      {course?.price} EGP
                    </p>
                  </>
                )}
              </div>
            )}
            <hr className="mb-3 mt-2" />
            <div className="d-flex gap-3 justify-content-between">
              {(() => {
                if (
                  isStudentEnrolled ||
                  (!isFreeCourse && !isStudentEnrolled)
                ) {
                  return (
                    <Button
                      onClick={() => navigate(`${course?.id}/details`)}
                      variant="dark"
                      className="w-100"
                    >
                      {t("freeMaterialsPage.viewCourse")}
                    </Button>
                  )
                }
              })()}

              {!isStudentEnrolled && (
                <Button
                  onClick={() => {
                    if (isFreeCourse) {
                      enrollIntoFreeMaterialCourseMutation.mutate(course, {
                        onSuccess: () => navigate(`${course?.id}/details`),
                      })
                    } else {
                      window.open(enrollUrl, "_blank")
                    }
                  }}
                  variant="primary"
                  className="w-100"
                  disabled={enrollIntoFreeMaterialCourseMutation.isPending}
                >
                  {enrollIntoFreeMaterialCourseMutation.isPending && (
                    <Spinner animation="border" className="me-2" size="sm" />
                  )}
                  {t("freeMaterialsPage.enrollIntoCourse")}
                </Button>
              )}
            </div>
          </div>
        </Card.Body>
      </Card>
    </Col>
  )
}
